"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { CartBadgeLink } from "@/components/cart/cart-badge-link";
import { useSession } from "next-auth/react";

const leftNavItems = [
  { href: "/boutique", label: "Boutique" },
  { href: "/qui-suis-je", label: "Qui suis-je" },
  { href: "/sources-d-inspiration", label: "Inspiration" },
];

const rightNavItems = [
  { href: "/travail-de-teinturiere", label: "Teinture" },
  { href: "/specificites-des-produits", label: "Produits" },
  { href: "/contact", label: "Contact" },
];


export function SiteHeader() {
  const pathname = usePathname();
  const { data: session } = useSession();

  const [isScrolled, setIsScrolled] = useState(false);
  const [isCompact, setIsCompact] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    let lastY = window.scrollY;
    let ticking = false;

    const updateHeader = () => {
      const currentY = window.scrollY;
      const delta = currentY - lastY;

      setIsScrolled((prev) => {
        if (prev) return currentY > 4;
        return currentY > 14;
      });

      setIsCompact((prev) => {
        if (prev) return currentY > 14;
        return currentY > 54;
      });

      setIsVisible((prev) => {
        if (currentY <= 18) return true;
        if (delta > 10) return false;
        if (delta < -10) return true;
        return prev;
      });

      lastY = currentY;
      ticking = false;
    };

    const onScroll = () => {
      if (Math.abs(window.scrollY - lastY) < 2) return;

      if (!ticking) {
        window.requestAnimationFrame(updateHeader);
        ticking = true;
      }
    };

    updateHeader();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const desktopRightItems = [...rightNavItems];

  return (
    <header
      className={[
        "sticky top-0 z-50 border-b border-[var(--border)]/70 transition-[background-color,box-shadow,border-color] duration-500 ease-out",
        isScrolled
          ? "bg-[#f4d7e7]"
          : "bg-[#f4d7e7]",
      ].join(" ")}
    >

      <div
        className={[
          "relative w-full overflow-x-clip px-6 transition-[height,padding] duration-500 ease-out sm:px-8 lg:px-10 xl:px-14 2xl:px-20",
          isCompact ? "h-[104px]" : "h-[132px]",
        ].join(" ")}
      >
        <div className="relative flex h-full items-center justify-between">
          <div className="flex min-w-0 flex-1 items-center pr-[clamp(120px,10vw,180px)]">
            <nav className="hidden w-full min-w-0 items-center justify-between lg:flex">
              {leftNavItems.map((item) => {
                const isActive = pathname === item.href || pathname.startsWith(`${item.href}/`);

                return (
                  <HeaderNavLink key={item.href} href={item.href} active={isActive} compact={isCompact}>
                    {item.label}
                  </HeaderNavLink>
                );
              })}
            </nav>
          </div>

          <div className="pointer-events-none absolute left-1/2 top-1/2 z-[1] -translate-x-1/2 -translate-y-1/2">
            <Link
              href="/"
              className="group pointer-events-auto relative flex flex-col items-center justify-center px-2 py-1"
              aria-label="Accueil Jour J Création"
            >
              <div
                className={[
                  "relative transition duration-500 group-hover:-translate-y-0.5",
                  isCompact ? "h-[64px] w-[64px]" : "h-[88px] w-[88px]",
                ].join(" ")}
              >
                <div className="absolute inset-0 rounded-full bg-[radial-gradient(circle_at_30%_30%,rgba(255,255,255,0.85),rgba(255,255,255,0.28),transparent_72%)] opacity-80 blur-[1px] transition duration-500 group-hover:scale-110" />
                <Image
                  src="/images/logo.avif"
                  alt="Jour J Création"
                  fill
                  sizes="88px"
                  className="object-contain drop-shadow-[0_10px_20px_rgba(50,35,35,0.08)] transition duration-500 group-hover:scale-[1.03]"
                  priority
                />
              </div>

              <span
                className={[
                  "mt-2 whitespace-nowrap font-mono uppercase tracking-[0.34em] text-[var(--foreground)]/56 transition duration-500 group-hover:text-[var(--accent)]",
                  isCompact ? "text-[10px]" : "text-[11px]",
                ].join(" ")}
              >
                Laines teintes à la main
              </span>
            </Link>
          </div>

          <div className="flex min-w-0 flex-1 items-center justify-end pl-[clamp(120px,10vw,180px)]">
            <div className="grid w-full min-w-0 overflow-x-clip grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
              <nav className="hidden min-w-0 items-center justify-between lg:flex">
                {desktopRightItems.map((item) => {
                  const isActive = pathname === item.href || pathname.startsWith(`${item.href}/`);

                  return (
                    <HeaderNavLink key={item.href} href={item.href} active={isActive} compact={isCompact}>
                      {item.label}
                    </HeaderNavLink>
                  );
                })}
              </nav>

              <div
                className={[
                  "flex shrink-0 items-center gap-2 sm:gap-2.5 transition-[opacity,transform,max-width] duration-500 ease-out will-change-[opacity,transform]",
                  isVisible
                    ? "max-w-[220px] translate-x-0 translate-y-0 opacity-100"
                    : "max-w-0 translate-x-4 translate-y-[-6px] overflow-hidden opacity-0 pointer-events-none",
                ].join(" ")}
                aria-hidden={!isVisible}
              >
                <CartBadgeLink />

                <Link
                  href={session ? "/compte" : "/connexion"}
                  aria-label="Mon compte"
                  title={session ? "Mon compte" : "Se connecter"}
                  className={[
                    "inline-flex items-center justify-center rounded-full border border-[var(--primary)]/18 bg-[linear-gradient(145deg,var(--primary),#ff5ca0)] text-white shadow-[0_12px_24px_rgba(255,63,142,0.22)] transition duration-300 hover:-translate-y-0.5 hover:scale-[1.02] hover:opacity-95",
                    isCompact ? "h-10 w-10" : "h-12 w-12",
                  ].join(" ")}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.9"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-[20px] w-[20px]"
                  >
                    <path d="M20 21a8 8 0 0 0-16 0" />
                    <circle cx="12" cy="8" r="4" />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

    </header>
  );
}

function HeaderNavLink({
  href,
  active,
  compact,
  children,
}: {
  href: string;
  active: boolean;
  compact: boolean;
  children: React.ReactNode;
}) {
  return (
    <Link
      href={href}
      className={[
        "group relative whitespace-nowrap rounded-full transition duration-300",
        compact
          ? "px-3 py-2 text-[15px] font-medium uppercase tracking-[0.2em]"
          : "px-4 py-3 text-[18px] font-medium uppercase tracking-[0.2em]",
        active
          ? "bg-[rgba(217,90,47,0.14)] text-[var(--accent)] shadow-[0_10px_22px_rgba(217,90,47,0.16)]"
          : "text-[var(--foreground)]/78 hover:-translate-y-0.5 hover:bg-[#f4d7e7] hover:text-[var(--primary)]",
      ].join(" ")}
    >
      <span className="relative z-[1]">{children}</span>
      {!active && (
        <span className="pointer-events-none absolute inset-x-1 bottom-1.5 h-px origin-left scale-x-0 bg-[linear-gradient(to_right,var(--accent),rgba(217,90,47,0.18))] transition duration-300 group-hover:scale-x-100" />
      )}
    </Link>
  );
}